import React, { createContext, useContext, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, Loader2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SITE } from "../i18n/translations";
import { SERVICES, getService } from "../data/services";
import api from "../lib/api";
import { toast } from "sonner";

const BookingContext = createContext(null);

export function BookingProvider({ children }) {
  const { t, pick, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [serviceId, setServiceId] = useState("");
  const [presetConcern, setPresetConcern] = useState("");
  const [form, setForm] = useState({ name: "", location: "", phone: "", birth: "", concern: "" });
  const [submitting, setSubmitting] = useState(false);

  const openBooking = useCallback((sid = "", concern = "") => {
    setServiceId(sid);
    setPresetConcern(concern);
    setForm((f) => ({ ...f, concern: concern || "" }));
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const service = getService(serviceId);
  const serviceTitle = service ? pick(service, "title") : "General Enquiry";

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(t("required"));
      return;
    }
    setSubmitting(true);

    const lines = [
      `🙏 ${SITE.name} — Enquiry`,
      `Service: ${serviceTitle}`,
      `Name: ${form.name}`,
      form.location ? `Location: ${form.location}` : null,
      form.phone ? `Phone: ${form.phone}` : null,
      form.birth ? `Birth details: ${form.birth}` : null,
      form.concern ? `Concern: ${form.concern}` : null,
    ].filter(Boolean);
    const message = lines.join("\n");

    try {
      await api.post("/enquiries", {
        name: form.name,
        location: form.location,
        phone: form.phone,
        language: lang,
        service_id: serviceId,
        service_title: serviceTitle,
        concern: form.concern,
        birth_details: form.birth,
        source: "website",
      });
    } catch (err) {
      // Non-blocking: still open WhatsApp even if save fails
      console.error("enquiry save failed", err);
    }

    const url = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(t("book_success"));
    setSubmitting(false);
    setOpen(false);
    setForm({ name: "", location: "", phone: "", birth: "", concern: "" });
  };

  return (
    <BookingContext.Provider value={{ openBooking }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="booking-overlay"
          >
            <div className="absolute inset-0 bg-[#14262A]/55 backdrop-blur-sm" onClick={close} />
            <motion.div
              className="glass-strong glass-reflect relative w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 max-h-[92vh] overflow-y-auto"
              initial={{ y: 60, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 60, opacity: 0, scale: 0.98 }}
              transition={{ type: "spring", damping: 26, stiffness: 260 }}
              data-testid="booking-drawer"
            >
              <button
                onClick={close}
                className="absolute right-4 top-4 text-tara-muted hover:text-tara-gold transition-colors"
                data-testid="booking-close-btn"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              <p className="font-serif text-xs tracking-[0.25em] uppercase text-tara-gold/90 mb-1">
                {t("field_service")}
              </p>
              <h3 className="font-display text-3xl font-semibold text-tara-ink mb-1">{t("book_title")}</h3>
              <p className="text-sm text-tara-muted mb-5">{t("book_sub")}</p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
                <div>
                  <label className="text-xs text-tara-muted mb-1 block">{t("field_service")}</label>
                  <select
                    value={serviceId}
                    onChange={(e) => setServiceId(e.target.value)}
                    className="w-full rounded-xl bg-white/70 border border-tara-gold/25 px-4 py-3 text-sm text-tara-ink focus:outline-none focus:border-tara-gold"
                    data-testid="booking-service-select"
                  >
                    <option value="">{`— ${t("services_eyebrow")} —`}</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.id}>{pick(s, "title")}</option>
                    ))}
                  </select>
                </div>

                <input
                  className="w-full rounded-xl bg-white/70 border border-tara-gold/25 px-4 py-3 text-sm text-tara-ink placeholder:text-tara-muted focus:outline-none focus:border-tara-gold"
                  placeholder={t("field_name")}
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  data-testid="booking-name-input"
                />
                <input
                  className="w-full rounded-xl bg-white/70 border border-tara-gold/25 px-4 py-3 text-sm text-tara-ink placeholder:text-tara-muted focus:outline-none focus:border-tara-gold"
                  placeholder={t("field_location")}
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                  data-testid="booking-location-input"
                />
                <input
                  className="w-full rounded-xl bg-white/70 border border-tara-gold/25 px-4 py-3 text-sm text-tara-ink placeholder:text-tara-muted focus:outline-none focus:border-tara-gold"
                  placeholder={t("field_phone")}
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  data-testid="booking-phone-input"
                />
                <input
                  className="w-full rounded-xl bg-white/70 border border-tara-gold/25 px-4 py-3 text-sm text-tara-ink placeholder:text-tara-muted focus:outline-none focus:border-tara-gold"
                  placeholder={t("field_birth")}
                  value={form.birth}
                  onChange={(e) => update("birth", e.target.value)}
                  data-testid="booking-birth-input"
                />
                <textarea
                  rows={3}
                  className="w-full rounded-xl bg-white/70 border border-tara-gold/25 px-4 py-3 text-sm text-tara-ink placeholder:text-tara-muted focus:outline-none focus:border-tara-gold resize-none"
                  placeholder={t("field_concern")}
                  value={form.concern}
                  onChange={(e) => update("concern", e.target.value)}
                  data-testid="booking-concern-input"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="aura-pulse w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-tara-gold to-tara-vermilion px-6 py-4 font-serif font-semibold text-white tracking-wide hover:brightness-110 transition disabled:opacity-70"
                  data-testid="booking-submit-btn"
                >
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <MessageCircle className="h-5 w-5" />}
                  {submitting ? t("submitting") : t("submit_whatsapp")}
                </button>
                <p className="text-[11px] text-center text-tara-muted leading-relaxed">{t("disclaimer_short")}</p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error("useBooking must be used within BookingProvider");
  return ctx;
}
